import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'

interface IProps {
  closeModal: () => void
  selectedBot: any
}

const CreateBotSchema = Yup.object().shape({
  botName: Yup.string()
    .trim()
    .min(2, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .matches(/^[\w\s.,!?'"-]+$/, 'Invalid characters in name')
    .required('Name is required'),

  botDescription: Yup.string()
    .trim()
    .min(2, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .matches(/^[\w\s.,!?'"-]+$/, 'Invalid characters in description')
    .required('Description is required'),

  startDate: Yup.date().nullable().typeError('Invalid date format'),

  endDate: Yup.date()
    .nullable()
    .typeError('Invalid date format')
    .min(Yup.ref('startDate'), 'End date cannot be before start date')
})

const CreateWhatsAppBotModal: React.FC<IProps> = ({ closeModal, selectedBot }) => {
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues: {
      botName: '',
      botDescription: '',
      startDate: '',
      endDate: ''
    },
    enableReinitialize: true,
    validationSchema: CreateBotSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        const token = localStorage.getItem('auth-token')
        const payload = {
          templateId: selectedBot?.[0]?.id,
          botName: values.botName,
          botDescription: values.botDescription,
          startDate: values.startDate,
          endDate: values.endDate
        }

        await axios.post(`${process.env.REACT_APP_RESUME_PARSER_URL}/whatsapp/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        })

        resetForm()
        closeModal()
        navigate('/whatsapp-bots/all-bots')
      } catch (error) {
        console.error('Failed to create bot:', error)
      } finally {
        setSubmitting(false)
      }
    }
  })

  return (
    <div>
      <div className='modal-content shadow-lg rounded'>
        <div className='modal-header'>
          <h5 className='modal-title'>
            Create WhatsApp Bot – {selectedBot?.[0]?.templateName || 'Template'}
          </h5>
          <button
            type='button'
            className='btn-close'
            aria-label='Close'
            onClick={closeModal}
          ></button>
        </div>

        <div className='modal-body'>
          <form onSubmit={formik.handleSubmit}>
            {/* Bot Name */}
            <div className='mb-3'>
              <label className='form-label'>
                WhatsApp Bot Name<span className='text-danger'> *</span>
              </label>
              <input
                {...formik.getFieldProps('botName')}
                type='text'
                placeholder='Enter bot name'
                className={clsx(
                  'form-control',
                  formik.touched.botName && formik.errors.botName && 'is-invalid'
                )}
              />
              {formik.touched.botName && formik.errors.botName && (
                <div className='invalid-feedback'>{formik.errors.botName}</div>
              )}
            </div>

            {/* Bot Description */}
            <div className='mb-3'>
              <label className='form-label'>
                WhatsApp Bot Description<span className='text-danger'> *</span>
              </label>
              <textarea
                {...formik.getFieldProps('botDescription')}
                placeholder='Enter bot description'
                className={clsx(
                  'form-control',
                  formik.touched.botDescription && formik.errors.botDescription && 'is-invalid'
                )}
                style={{ height: '100px', resize: 'none' }}
              />
              {formik.touched.botDescription && formik.errors.botDescription && (
                <div className='invalid-feedback'>{formik.errors.botDescription}</div>
              )}
            </div>

            {/* Start Date */}
            <div className='mb-3'>
              <label className='form-label'>Start Date</label>
              <input {...formik.getFieldProps('startDate')} type='date' className='form-control' />
            </div>

            {/* End Date */}
            <div className='mb-3'>
              <label className='form-label'>End Date</label>
              <input
                {...formik.getFieldProps('endDate')}
                type='date'
                min={formik.values.startDate || new Date().toISOString().split('T')[0]}
                className={clsx(
                  'form-control',
                  formik.touched.endDate && formik.errors.endDate && 'is-invalid'
                )}
              />
              {formik.touched.endDate && formik.errors.endDate && (
                <div className='invalid-feedback'>{formik.errors.endDate}</div>
              )}
            </div>

            {/* Buttons */}
            <div className='d-flex justify-content-end gap-2'>
              <button type='button' className='btn btn-outline-secondary' onClick={closeModal}>
                Cancel
              </button>
              <button
                type='submit'
                disabled={formik.isSubmitting || !formik.isValid}
                className='btn btn-primary'
              >
                {formik.isSubmitting ? 'Creating...' : 'Create Bot'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateWhatsAppBotModal
